import { supabase } from '../supabase'
import { BlobWriter, ZipWriter, TextReader } from '@zip.js/zip.js'

async function fetchFullPatientExport(patientId) {
  const [
    { data: patient },
    { data: goals },
    { data: metrics },
    { data: skinfolds },
    { data: activityProfile },
    { data: notes },
    { data: alerts },
    { data: activityLogs },
    { data: dailyLogs },
  ] = await Promise.all([
    supabase.from('patients').select('*').eq('id', patientId).single(),
    supabase.from('patient_goals').select('*').eq('paciente_id', patientId),
    supabase.from('body_metrics').select('*').eq('paciente_id', patientId).order('fecha'),
    supabase.from('skinfolds').select('*').eq('paciente_id', patientId).order('fecha'),
    supabase.from('activity_profile').select('*').eq('paciente_id', patientId),
    supabase.from('clinical_notes').select('*').eq('paciente_id', patientId).order('fecha_revision'),
    supabase.from('alerts').select('*').eq('paciente_id', patientId).order('fecha'),
    supabase.from('activity_logs').select('*').eq('paciente_id', patientId).order('fecha'),
    supabase.from('daily_logs').select(`
      id, fecha,
      meals ( id, tipo, meal_items ( cantidad_gramos, foods ( id, nombre, kcal_100g, proteina_100g, grasa_100g, carbohidratos_100g ) ) )
    `).eq('paciente_id', patientId).order('fecha'),
  ])

  return {
    patient,
    goals: goals?.[0] ?? null,
    metrics: metrics ?? [],
    skinfolds: skinfolds ?? [],
    activityProfile: activityProfile?.[0] ?? null,
    notes: notes ?? [],
    alerts: alerts ?? [],
    activityLogs: activityLogs ?? [],
    dailyLogs: dailyLogs ?? [],
  }
}

function csvCell(v) {
  if (v == null) return ''
  const s = String(v)
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}
function csvRow(values) {
  return values.map(csvCell).join(',')
}
function csvTable(header, rows) {
  return [csvRow(header), ...rows.map(csvRow)].join('\n')
}

function buildPacientesCsv(exports) {
  const header = ['id', 'nombre', 'sexo', 'fecha_nacimiento', 'altura_cm', 'objetivo_tipo', 'kcal_objetivo', 'proteina_objetivo_g', 'grasa_objetivo_g', 'carbohidratos_objetivo_g', 'peso_objetivo_kg', 'grasa_objetivo_pct']
  const rows = exports.map(({ patient, goals }) => [
    patient.id, patient.nombre, patient.sexo, patient.fecha_nacimiento, patient.altura_cm,
    goals?.tipo, goals?.kcal_objetivo, goals?.proteina_objetivo, goals?.grasa_objetivo,
    goals?.carbohidratos_objetivo, goals?.peso_objetivo, goals?.grasa_objetivo_pct,
  ])
  return csvTable(header, rows)
}

function buildMedicionesCsv(exports) {
  const header = ['paciente_id', 'paciente_nombre', 'fecha', 'peso_kg', 'grasa_pct', 'masa_magra_pct', 'masa_muscular_kg', 'perimetro_abdominal_cm', 'metodo']
  const rows = []
  exports.forEach(({ patient, metrics }) => {
    metrics.forEach(m => rows.push([
      patient.id, patient.nombre, m.fecha, m.peso_kg, m.grasa_pct, m.masa_magra_pct, m.masa_muscular_kg, m.perimetro_abdominal_cm, m.metodo,
    ]))
  })
  return csvTable(header, rows)
}

function mealItemTotals(dailyLogs) {
  // Devuelve, por cada daily_log, los items ya "aplanados" con sus valores por ración calculados
  return dailyLogs.map(log => {
    const items = (log.meals ?? []).flatMap(m => (m.meal_items ?? []).map(item => ({ ...item, tipo: m.tipo })))
    return { fecha: log.fecha, items }
  })
}

function buildRegistroDiarioCsv(exports) {
  const header = ['paciente_id', 'paciente_nombre', 'fecha', 'kcal_total', 'proteina_total_g', 'grasa_total_g', 'carbohidratos_total_g']
  const rows = []
  exports.forEach(({ patient, dailyLogs }) => {
    mealItemTotals(dailyLogs).forEach(({ fecha, items }) => {
      const sum = key => items.reduce((s, i) => s + ((i.foods?.[key] ?? 0) * i.cantidad_gramos / 100), 0)
      rows.push([
        patient.id, patient.nombre, fecha,
        Math.round(sum('kcal_100g')),
        Math.round(sum('proteina_100g') * 10) / 10,
        Math.round(sum('grasa_100g') * 10) / 10,
        Math.round(sum('carbohidratos_100g') * 10) / 10,
      ])
    })
  })
  return csvTable(header, rows)
}

function buildComidasDetalleCsv(exports) {
  const header = ['paciente_id', 'paciente_nombre', 'fecha', 'tipo_comida', 'alimento', 'cantidad_gramos', 'kcal', 'proteina_g', 'grasa_g', 'carbohidratos_g']
  const rows = []
  exports.forEach(({ patient, dailyLogs }) => {
    mealItemTotals(dailyLogs).forEach(({ fecha, items }) => {
      items.forEach(item => {
        const f = item.foods
        rows.push([
          patient.id, patient.nombre, fecha, item.tipo, f?.nombre, item.cantidad_gramos,
          Math.round((f?.kcal_100g ?? 0) * item.cantidad_gramos / 100),
          Math.round((f?.proteina_100g ?? 0) * item.cantidad_gramos / 100 * 10) / 10,
          Math.round((f?.grasa_100g ?? 0) * item.cantidad_gramos / 100 * 10) / 10,
          Math.round((f?.carbohidratos_100g ?? 0) * item.cantidad_gramos / 100 * 10) / 10,
        ])
      })
    })
  })
  return csvTable(header, rows)
}

function buildActividadCsv(exports) {
  const header = ['paciente_id', 'paciente_nombre', 'fecha', 'tipo', 'duracion_min', 'kcal_estimadas', 'notas']
  const rows = []
  exports.forEach(({ patient, activityLogs }) => {
    activityLogs.forEach(a => rows.push([patient.id, patient.nombre, a.fecha, a.tipo, a.duracion_min, a.kcal_estimadas, a.notas]))
  })
  return csvTable(header, rows)
}

function safeFileName(name) {
  return (name || 'paciente').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\w-]+/g, '_')
}

export async function exportPatientsToZip(patients, password, onProgress, anonymize = false) {
  const exports = []
  for (const p of patients) {
    exports.push(await fetchFullPatientExport(p.id))
    onProgress?.(exports.length, patients.length)
  }

  if (anonymize) {
    exports.forEach((exp, i) => {
      if (!exp.patient) return
      exp.patient.nombre = `PAC-${String(i + 1).padStart(3, '0')}`
      delete exp.patient.user_id
    })
  }

  const zipWriter = new ZipWriter(new BlobWriter('application/zip'), {
    password: password || undefined,
    encryptionStrength: 3,
  })

  for (const exp of exports) {
    await zipWriter.add(`json/${safeFileName(exp.patient?.nombre)}.json`, new TextReader(JSON.stringify(exp, null, 2)))
  }
  await zipWriter.add('pacientes.csv', new TextReader(buildPacientesCsv(exports)))
  await zipWriter.add('mediciones.csv', new TextReader(buildMedicionesCsv(exports)))
  await zipWriter.add('registro_diario.csv', new TextReader(buildRegistroDiarioCsv(exports)))
  await zipWriter.add('comidas_detalle.csv', new TextReader(buildComidasDetalleCsv(exports)))
  await zipWriter.add('actividad_fisica.csv', new TextReader(buildActividadCsv(exports)))

  return zipWriter.close()
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
